import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FailurePhaseBadge } from "@/components/ci/failure-phase-badge";
import { CiStatusBadge } from "@/components/ci/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { CiTestResult } from "@/lib/types";
import { cn, formatMs } from "@/lib/utils";

interface MatrixCellGroupProps {
  cellId: string;
  axisValues: Record<string, string>;
  results: CiTestResult[];
  runId: string;
}

// Vanilla start-attribution probe verdict (bench vanilla_probe.py), rendered
// next to the failure-phase badge. ✓ = the workload boots unmanaged, so the
// start failure is attributed to cryod management overhead; ✗ = it fails
// vanilla too with an OOM/crash (recipe/sizing doesn't fit); ? = the rerun
// died without an OOM/crash signature (timeout, infra) — inconclusive.
// Diagnostic — the row's status badge is unaffected.
function VanillaStartChip({ result }: { result: CiTestResult }) {
  const verdict = result.vanilla_start_status;
  if (verdict !== "pass" && verdict !== "fail" && verdict !== "inconclusive") {
    return null;
  }
  const title =
    verdict === "pass"
      ? "unmanaged (vanilla) rerun reached Ready — failure attributed to cryod management"
      : verdict === "fail"
        ? (result.vanilla_start_error ?? "unmanaged (vanilla) rerun also failed")
        : (result.vanilla_start_error ??
          "unmanaged (vanilla) rerun failed without an OOM/crash signature — inconclusive");
  const chipClass =
    verdict === "pass"
      ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-500"
      : verdict === "fail"
        ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
        : "border-amber-500/30 bg-amber-500/10 text-amber-400";
  const glyph = verdict === "pass" ? "✓" : verdict === "fail" ? "✗" : "?";
  return (
    <span title={title} className={cn("rounded border px-1 font-mono text-[10px]", chipClass)}>
      {glyph} vanilla
    </span>
  );
}

function formatNodes(result: CiTestResult): string {
  const nodes = result.nodes;
  if (!nodes) return result.machine_name || "—";
  const source = nodes.source;
  const restore = nodes.restore;
  if (nodes.multi && nodes.multi.length > 1) {
    return `${source ?? nodes.multi[0]} → ${nodes.multi.join(", ")}`;
  }
  if (source && restore && source !== restore) {
    return `${source} → ${restore}`;
  }
  return source ?? restore ?? result.machine_name ?? "—";
}

export function MatrixCellGroup({ cellId, axisValues, results, runId }: MatrixCellGroupProps) {
  // Restore / K8s / Transfer / Nodes are cryod restore phases; a Mooncake
  // suite has none, so a group made only of those shows just the verdicts.
  const showPhases = results.some((result) => result.operation !== "mctest");
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const counts = useMemo(() => {
    const passed = results.filter((result) => result.status === "pass").length;
    const failed = results.filter((result) => result.status === "fail").length;
    const skipped = results.filter((result) => result.status === "skip").length;
    return { passed, failed, skipped };
  }, [results]);

  const sorted = useMemo(() => {
    const priority: Record<string, number> = { fail: 0, pass: 1, skip: 2 };
    return [...results].sort(
      (first, second) => (priority[first.status] ?? 3) - (priority[second.status] ?? 3),
    );
  }, [results]);

  const axisKeys = Object.keys(axisValues);

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-accent/40 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {axisKeys.length > 0 ? (
            axisKeys.map((key) => (
              <span
                key={key}
                className="inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px]"
              >
                <span className="text-muted-foreground uppercase tracking-wider">{key}</span>
                <span className="font-mono text-foreground">{axisValues[key]}</span>
              </span>
            ))
          ) : (
            <span className="font-mono text-sm">{cellId || "default"}</span>
          )}
          <div className="flex items-center gap-1.5 text-xs">
            {counts.passed > 0 && <span className="text-emerald-500">{counts.passed} passed</span>}
            {counts.failed > 0 && <span className="text-rose-500">{counts.failed} failed</span>}
            {counts.skipped > 0 && (
              <span className="text-muted-foreground">{counts.skipped} skipped</span>
            )}
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{collapsed ? "Show" : "Hide"}</span>
      </button>
      {!collapsed && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Test</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              {showPhases && (
                <>
                  <TableHead className="text-right">Restore</TableHead>
                  <TableHead className="text-right">K8s</TableHead>
                  <TableHead className="text-right">Transfer</TableHead>
                  <TableHead>Nodes</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((result) => (
              <TableRow
                key={result.id}
                className={cn(
                  "cursor-pointer",
                  result.status === "fail" && "border-l-2 border-l-rose-500",
                )}
                onClick={() => navigate(`/ci/${runId}/tests/${result.id}`)}
              >
                <TableCell>
                  <div>
                    <span className="font-mono text-sm">{result.test_name}</span>
                    {result.status === "fail" && result.error && (
                      <p className="mt-0.5 max-w-md truncate text-xs text-rose-500">
                        {result.error}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <CiStatusBadge status={result.status} />
                    {result.status === "fail" && result.failure_phase && (
                      <FailurePhaseBadge phase={result.failure_phase} />
                    )}
                    <VanillaStartChip result={result} />
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {result.duration_seconds > 0
                    ? `${result.duration_seconds.toFixed(result.duration_seconds < 1 ? 2 : 1)}s`
                    : result.operation === "mctest" &&
                        result.status !== "skip" &&
                        result.test_name !== "mctest.harness" // the synthetic row has no Go timing
                      ? "0.00s" // Go's own timing: the suite really took under 5 ms
                      : "-"}
                </TableCell>
                {showPhases && (
                  <>
                    <TableCell className="text-right font-mono text-sm">
                      {formatMs(result.total_restore_ms)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatMs(result.kubernetes_overhead_ms)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatMs(result.mooncake_transfer_ms)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {formatNodes(result)}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
